/**
 * Canonical route table for the app, in the shape expected by `react-router-config`'s
 * `renderRoutes()` (path/exact/component/render, same as a JSX <Route>, just as data).
 *
 * This being plain data is what makes it introspectable: `routes.map(r => r.path)` is a
 * full sitemap without parsing JSX or booting the app.
 *
 * `meta` is descriptive only (grouping for docs/sitemaps) -- it isn't read by the router.
 * Whether a route requires auth is still decided by `authPage`/`authed` below, exactly as
 * it was in app.js; nothing about that behaviour has changed, only where it's written.
 */

import { Redirect } from "react-router-dom";
import { clearStorage, getUUID, isLoggedIn, updateSessionExpiry } from './utils/auth';

// Pages
import Homepage from './pages/homepage';
import Dashboard from './pages/dashboard';
import NotFound from "./pages/not_found";
import Notifications from "./pages/notifications";
// Signup
import Login from './pages/user/login';
import SignUp from "./pages/user/signup";
import Logout from './pages/user/logout';
import VerifyEmail from './pages/user/verify_email';
import { ResendEmail } from "./components/resend_email";
import ForgotPassword from "./pages/user/forgot_password";
import ResetPassword from "./pages/user/reset_password";
// Connections
import Connections from "./pages/connections/connections";
import AcceptConnectionInvite from "./pages/connections/accept_invitation";
import { OnboardAdult } from './pages/connections/onboard_adult';
import { OnboardDependant } from './pages/connections/onboard_dependant';
// Records
import Records from "./pages/records/records";
import RecordsUser from "./pages/records/user";
// Sharing
import Sharing from "./pages/sharing/sharing";
import ShareEmail from "./pages/sharing/share_email";
import ShareConfirm from "./pages/sharing/share_confirm";
import InviteError from "./pages/invite_error";
// Health
import AddCovidVaccine from "./pages/health/add_covid_vaccine";
// Family
import DependantScanner from "./pages/family/scan_dependant";
import VerifyShare from './pages/family/verify_share';
import AddDependants from "./pages/family/add_dependants";
// Discover
import Discover from './pages/discover/discover'
import { VaccinationScheduleHKG, VaccinationScheduleGBRPreJuly2024, VaccinationScheduleGBRPostJuly2024 } from './pages/discover/vaccination_schedule'
// Travel
import { TravelItinerary, ItineraryVaccines, TravelAddDestination } from './pages/travel/itinerary'
// Test
import Templates from './pages/test_pages/templates';
import TestSharingDependants from "./pages/test_pages/test_share_dependant";
import TestSendNotification from "./pages/test_pages/test_send_notification";
import { PageScrollingDemo } from "./pages/test_pages/page_scrolling/test_page_scrolling";
import { NoShadowDemo } from "./pages/test_pages/page_scrolling/test_no_shadow_scrolling";
import { ElevationScrollingDemo } from "./pages/test_pages/page_scrolling/test_elevation_scrolling";
import { FloatButtonDemo } from "./pages/test_pages/page_scrolling/test_float_button_scolling";
import TestCreatePupil from "./pages/test_pages/test_create_pupil";
import { ShowNavCta, ShowCtaOnly, ShowNavOnly } from "./pages/test_pages/test_bottom";
import TestClinicInvite from "./pages/test_pages/test_clinic_invite";
import TestGenerateError from "./pages/test_pages/test_generate_error";
import TestResetIntro from "./pages/test_pages/test_reset_intro";
import TestResetEMRUser from "./pages/test_pages/test_reset_emr_user";
import TestSchemaViolation from "./pages/test_pages/test_schema_violation";

import { AddRecord } from './pages/records/patient_certified/add_record';
import { AddDtap } from './pages/records/patient_certified/add_dtap';
import { AddVaccineRecord, AddPaperRecords } from './pages/add_vaccine_record'
import { AddPaperRecords as AddPaperRecordsWizard, Step as PaperRecordStep } from './pages/records/paper/new';
import { SubmissionHistory } from './pages/records/paper/history';
import { SubmissionView } from './pages/records/paper/view';

import Editor from "./pages/editor";
import { AccountSettings } from './pages/user/account_settings';
import { InvitedDependantsDashboard } from './pages/invited_dependants';
import { MissingDTaP } from './pages/missing_dtap';
import { TestDeepLinks } from './pages/test_pages/test_deep_links';

import { GillickReview, GillickSetEmail } from './pages/gillick/review';

/** Requires a logged in user to access the route. Redirects to the log in page otherwise. */
export function authPage(Destination, props) {
    // If a user is logged in but we have no UUID, then their session information
    // has likely been corrupted by a race condition. Remove their session to force
    // them to login again and reset their state
    if (isLoggedIn() && !getUUID()) {
        // Wipe local storage, effectively logging the user out
        // Calls to isLoggedIn will now return false
        clearStorage()
    }
    if (!isLoggedIn()) {
        // this prevents the user going to a page with null uuid in the url after logging in
        // the null uuid is an edge case when user has multiple tabs, logouts in one of them, then try to continue to action in other tabs
        if (props.location.pathname.includes('/null')) {
            return <Redirect to={'/login'} />
        }
        return <Redirect to={'/login?next='+props.location.pathname} />
    } else {
        // refreshes user's login session expiry time
        updateSessionExpiry();
        return <Destination {...props} />
    }
}

/** Prevents a logged in user from accessing the route. Redirects to the User dashboard otherwise. */
export function authed(Destination, props) {
    if(isLoggedIn()) {
        // refreshes user's login session expiry time
        updateSessionExpiry();
        return <Redirect to={'/home'} />
    } else {
        return <Destination {...props} />
    }
}

export const routes = [
    // Drops trailing slash from paths
    {
        path: "/*/",
        exact: true,
        strict: true,
        render: (props) => <Redirect to={`${props.location.pathname.slice(0, -1)}${props.location.search}${props.location.hash}`} />,
        meta: { group: "Internal", internal: true },
    },

    // Records pages
    { path: "/records", exact: true, render: (props) => authPage(Records, props), meta: { group: "Records" } },
    { path: "/records/:uuid/:tab(health|profile|vaccine|past-records)", exact: true, render: (props) => authPage(RecordsUser, props), meta: { group: "Records" } },

    // Health pages
    { path: "/scan", exact: true, render: (props) => authPage(AddCovidVaccine, props), meta: { group: "Health" } },
    { path: "/add-record/:uuid/:step(location|hemisphere|date|product|summary|hemisphere-cannot-proceed|date-cannot-proceed|location-cannot-proceed|record-duplicate)", exact: true, render: (props) => authPage(AddRecord, props), meta: { group: "Health" } },
    { path: "/add-dtap/:uuid/:step(record_type|record_details|ask_later|confirm_unvaccinated|dont_qualify)", exact: true, render: (props) => authPage(AddDtap, props), meta: { group: "Health" } },
    { path: "/add-vaccine-record", exact: true, render: (props) => authPage(AddVaccineRecord, props), meta: { group: "Health" } },
    { path: "/paper-records/new", exact: true, render: (props) => authPage(AddPaperRecords, props), meta: { group: "Health" } },
    { path: `/paper-records/new/:step(${Object.values(PaperRecordStep).join('|')})`, exact: true, render: (props) => authPage(AddPaperRecordsWizard, props), meta: { group: "Health" } },
    { path: "/paper-records/history", exact: true, render: (props) => authPage(SubmissionHistory, props), meta: { group: "Health" } },
    { path: "/paper-records/submission/:id", exact: true, render: (props) => authPage(SubmissionView, props), meta: { group: "Health" } },
    { path: "/paper-records/submission/:id/page/:pageNumber", exact: true, render: (props) => authPage(SubmissionView, props), meta: { group: "Health" } },

    // Family pages
    { path: "/dependants/scan", exact: true, render: (props) => authPage(DependantScanner, props), meta: { group: "Family" } },
    { path: "/dependants/add", exact: true, render: (props) => authPage(AddDependants, props), meta: { group: "Family" } },

    // Sharing pages
    { path: "/sharing/invite/:token", exact: true, component: VerifyShare, meta: { group: "Sharing" } },
    { path: "/sharing/confirm", exact: true, render: (props) => authPage(ShareConfirm, props), meta: { group: "Sharing" } },
    { path: "/sharing/send/email", exact: true, render: (props) => authPage(ShareEmail, props), meta: { group: "Sharing" } },
    { path: "/sharing", exact: true, render: (props) => authPage(Sharing, props), meta: { group: "Sharing" } },

    // Connections pages
    { path: "/connections", exact: true, render: (props) => authPage(Connections, props), meta: { group: "Connections" } },
    { path: "/connection/invite/:token", exact: true, component: AcceptConnectionInvite, meta: { group: "Connections" } },
    { path: "/connection/adult/:step", exact: true, component: OnboardAdult, meta: { group: "Connections" } },
    { path: "/connection/dependant/:step", exact: true, render: (props) => authPage(OnboardDependant, props), meta: { group: "Connections" } },

    // Sign up pages
    { path: "/signup", exact: true, component: SignUp, meta: { group: "Sign up" } },
    { path: "/signup/resend", exact: true, component: ResendEmail, meta: { group: "Sign up" } },
    { path: "/verify-email/:token", exact: true, component: VerifyEmail, meta: { group: "Sign up" } },
    { path: "/login", exact: true, render: (props) => authed(Login, props), meta: { group: "Sign up" } },
    { path: "/logout", exact: true, component: Logout, meta: { group: "Sign up" } },
    { path: "/forgot-password", exact: true, component: ForgotPassword, meta: { group: "Sign up" } },
    { path: "/reset-password/:uuid/:token", exact: true, component: ResetPassword, meta: { group: "Sign up" } },

    // Account settings pages
    { path: "/account-settings", exact: true, render: (props) => authPage(AccountSettings, props), meta: { group: "Account settings" } },

    // Home pages
    { path: "/notifications", exact: true, render: (props) => authPage(Notifications, props), meta: { group: "Home" } },
    { path: "/home", exact: true, render: (props) => authPage(Dashboard, props), meta: { group: "Home" } },
    { path: "/", exact: true, render: (props) => authed(Homepage, props), meta: { group: "Home" } },
    { path: "/pending-connections", exact: true, render: (props) => authPage(InvitedDependantsDashboard, props), meta: { group: "Home" } },
    { path: "/missing-dtap", exact: true, render: (props) => authPage(MissingDTaP, props), meta: { group: "Home" } },

    // Discover
    { path: "/discover", exact: true, render: (props) => authPage(Discover, props), meta: { group: "Discover" } },
    { path: "/discover/schedule/hkg", exact: true, render: (props) => authPage(VaccinationScheduleHKG, props), meta: { group: "Discover" } },
    { path: "/discover/schedule/gbr/pre_july_2024", exact: true, render: (props) => authPage(VaccinationScheduleGBRPreJuly2024, props), meta: { group: "Discover" } },
    { path: "/discover/schedule/gbr/post_july_2024", exact: true, render: (props) => authPage(VaccinationScheduleGBRPostJuly2024, props), meta: { group: "Discover" } },

    // Travel vaccines
    { path: "/travel/itinerary", exact: true, render: (props) => authPage(TravelItinerary, props), meta: { group: "Travel" } },
    { path: "/travel/vaccines", exact: true, render: (props) => authPage(ItineraryVaccines, props), meta: { group: "Travel" } },
    { path: "/travel/add-destination", exact: true, render: (props) => authPage(TravelAddDestination, props), meta: { group: "Travel" } },

    // Gillick competence
    { path: "/gillick_review", exact: true, render: (props) => authPage(GillickReview, props), meta: { group: "Gillick" } },
    { path: "/gillick_set_email", exact: true, render: (props) => authPage(GillickSetEmail, props), meta: { group: "Gillick" } },

    // Testing / internal pages
    { path: "/_/editor", exact: true, component: Editor, meta: { group: "Internal", internal: true } },
    { path: "/templates", exact: true, component: Templates, meta: { group: "Internal", internal: true } },
    { path: "/test-share-dependants", exact: true, render: (props) => authPage(TestSharingDependants, props), meta: { group: "Internal", internal: true } },
    { path: "/test-send-notification", exact: true, render: (props) => authPage(TestSendNotification, props), meta: { group: "Internal", internal: true } },
    { path: "/test-page-scrolling", exact: true, component: PageScrollingDemo, meta: { group: "Internal", internal: true } },
    { path: "/test-no-shadow-scrolling", exact: true, component: NoShadowDemo, meta: { group: "Internal", internal: true } },
    { path: "/test-elevation-scrolling", exact: true, component: ElevationScrollingDemo, meta: { group: "Internal", internal: true } },
    { path: "/test-float-button-scrolling", exact: true, component: FloatButtonDemo, meta: { group: "Internal", internal: true } },
    { path: "/test-create-pupil", exact: true, component: TestCreatePupil, meta: { group: "Internal", internal: true } },
    { path: "/test-nav", exact: true, component: ShowNavOnly, meta: { group: "Internal", internal: true } },
    { path: "/test-cta", exact: true, component: ShowCtaOnly, meta: { group: "Internal", internal: true } },
    { path: "/test-nav-cta", exact: true, component: ShowNavCta, meta: { group: "Internal", internal: true } },
    { path: "/test-clinic-invite", exact: true, render: (props) => authPage(TestClinicInvite, props), meta: { group: "Internal", internal: true } },
    { path: "/test-generate-error", exact: true, render: (props) => authPage(TestGenerateError, props), meta: { group: "Internal", internal: true } },
    { path: "/test-deep-links", exact: true, render: (props) => authPage(TestDeepLinks, props), meta: { group: "Internal", internal: true } },
    { path: "/test-reset-intro", exact: true, render: (props) => authPage(TestResetIntro, props), meta: { group: "Internal", internal: true } },
    { path: "/test-reset-emr-user", exact: true, component: TestResetEMRUser, meta: { group: "Internal", internal: true } },
    { path: "/test-schema-violation", exact: true, render: (props) => authPage(TestSchemaViolation, props), meta: { group: "Internal", internal: true } },

    // Error pages
    { path: "/invite/error", exact: true, component: InviteError, meta: { group: "Error" } },
    { component: NotFound, meta: { group: "Error" } }, // catch-all - must stay last
];
